import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./BeverageLandingPages.module.css";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useAcid } from "../../hooks/useAcid";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { sendPageViewWhenConsentReady } from "../../utils/adobeDataLayerUtils";
import { saveConsentAndContinue } from "./consentSave";

/**
 * Persists via the same save-beverage-preferences action used by every
 * other beverage flow's consent handling (e.g. BeverageV5's
 * saveConsentOnly) — the previous useConsentCDP/save-privacy-consents-CDP
 * path never actually reached a working endpoint, so consent was never
 * saved through it.
 */
const saveConsent = (acid, optedOut) =>
  actionWebInvoke(
    actions["marriott-passions-v3/save-beverage-preferences"],
    {},
    { acid, optedOutPersonalization: optedOut },
  );

export const BeverageOptConfirm = () => {
  const navigate = useNavigate();
  const { acid } = useAcid();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  /**
   * Synchronous lock: isSaving state only blocks re-entry after a render
   * commits, so a fast double-click could still fire two saves before then.
   */
  const consentLockRef = useRef(false);

  const handleConsentChoice = useCallback(
    async (optedOut) => {
      if (consentLockRef.current) {
        return;
      }
      consentLockRef.current = true;
      setIsSaving(true);
      setSaveError(null);

      const result = await saveConsentAndContinue({
        acid,
        optedOut,
        saveConsent,
        navigate,
      });

      if (!result.saved) {
        console.error("Error saving consent:", result.error);
        setSaveError("We couldn't save your preference. Please try again.");
      }

      consentLockRef.current = false;
      setIsSaving(false);
    },
    [acid, navigate],
  );

  const agree = useCallback(
    () => handleConsentChoice(false),
    [handleConsentChoice],
  );

  const disagree = useCallback(
    () => handleConsentChoice(true),
    [handleConsentChoice],
  );

  useEffect(() => {
    return sendPageViewWhenConsentReady("Beverage V2 Consent", 'Passions-FavBevs', 'beverages2');
  }, []);

  return (
    <main className={styles.main}>
      <figure className={classNames(styles.figure, styles.optConfirmFigure)}>
        <img
          className={styles.image}
          src="https://cache.marriott.com/is/image/marriotts7prod/bonvoy-points-redemption--33160?wid=1440"
          alt="Saved"
        />

        <figcaption className={classNames(styles.caption)}>
          <h2 className={classNames(styles.title, styles.sharingTitle)}>
            Saved!
          </h2>

          <p className={styles.secondTitle}>
            We’d love to make your next stay special...
          </p>
        </figcaption>
      </figure>

      {saveError && <p className={styles.errorMessage}>{saveError}</p>}

      <div className={styles.optButtons}>
        <button
          data-testid="consent-decline"
          className={`${styles.secondaryButton} custom_click_track`}
          data-custom_click_track_value="Beverage V2 Opt Confirm|No Thanks|internal"
          disabled={isSaving}
          onClick={disagree}
        >
          No, thanks
        </button>

        <button
          data-testid="consent-accept"
          onClick={agree}
          disabled={isSaving}
          className={`${styles.primaryButton} custom_click_track`}
          data-custom_click_track_value="Beverage V2 Opt Confirm|Yes Please|internal"
        >
          Yes, please
        </button>
      </div>

      <p className={classNames(styles.footer)}>
        We need your permission for the use of your preferences by the Marriott
        Group to deliver personalized services at properties.
      </p>
    </main>
  );
};
